import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Flame, Gift, Eye, Globe, Check, Video } from "lucide-react";
import api, { TIER_META, OCCASION_META, roasterFlair } from "../lib/api";
import RoasterAvatar from "../components/RoasterAvatar";
import ReactionRecorder from "../components/ReactionRecorder";

export default function Reveal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opened, setOpened] = useState(false);
  const [consenting, setConsenting] = useState(false);
  const [reaction, setReaction] = useState(null);

  const load = () => api.get(`/reveal/${token}`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  useEffect(() => { load(); }, [token]);

  const consent = async (val) => {
    setConsenting(true);
    try {
      await api.post(`/reveal/${token}/consent`, { consent: val });
      setData((d) => ({ ...d, consent_feed: val }));
      toast.success(val ? "Thanks — the gifter can now share it publicly." : "Kept private.");
    } catch (e) { toast.error("Couldn't save that"); }
    finally { setConsenting(false); }
  };

  if (loading) return <Center><div className="w-10 h-10 border-2 border-brand border-t-transparent rounded-full animate-spin" /></Center>;
  if (!data) return <Center><p className="text-zinc-400">This reveal link isn't valid.</p></Center>;

  const occ = OCCASION_META[data.occasion] || OCCASION_META.just_because;
  const meta = TIER_META[data.tier] || TIER_META.savage;

  if (!data.ready) return (
    <Center>
      <div className="text-center max-w-sm">
        <div className="text-5xl mb-4">{occ.emoji}</div>
        <h1 className="font-heading font-bold uppercase tracking-tighter text-3xl">Someone lined up a roast for you</h1>
        <p className="text-zinc-400 mt-3">{data.from ? `${data.from} sent it` : "An anonymous gifter sent it"} for your {occ.label.toLowerCase()}. The winning roast isn't picked yet — check back soon.</p>
      </div>
    </Center>
  );

  return (
    <div className="min-h-screen bg-background text-zinc-100 px-4 py-10">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-2 mb-8"><Flame className="text-brand" /><span className="font-heading font-bold tracking-tight">RoastLit</span></div>

        {!opened ? (
          <div className="text-center py-10">
            <div className="text-6xl mb-4">{occ.emoji}</div>
            <h1 className="font-heading font-bold uppercase tracking-tighter text-4xl">Someone roasted you for your {occ.label.toLowerCase()}</h1>
            <p className="text-zinc-400 mt-3">{data.from ? `From ${data.from}` : "From an anonymous gifter"} · brace yourself.</p>
            <button data-testid="reveal-open-btn" onClick={() => setOpened(true)}
              className="mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-brand hover:bg-brand-hover text-black font-bold text-lg transition-colors">
              <Eye size={20} /> Tap to see it
            </button>
          </div>
        ) : (
          <div className="animate-fade-up">
            <span className="text-[11px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full" style={{ color: meta.color, background: `${meta.color}1a` }}>{meta.label} · {occ.emoji} {occ.label}</span>
            {data.content_type === "image" && (
              <img src={data.content} alt="" className="mt-4 rounded-sm border border-white/10 max-h-52 object-cover" />
            )}
            <p data-testid="reveal-roast" className="text-2xl leading-relaxed text-zinc-50 mt-4 text-shadow-fire">"{data.roast?.content_text}"</p>
            <div className="flex items-center gap-2.5 mt-4">
              <RoasterAvatar avatar={data.roast?.roaster_avatar} initials="R" size={36} ring={meta.color} />
              <div className="leading-tight">
                <div className="text-sm" style={{ color: meta.color }}>{data.roast?.roaster_handle}</div>
                {roasterFlair(data.roast?.roaster_wins) && <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">{roasterFlair(data.roast?.roaster_wins)}</div>}
              </div>
            </div>

            <div className="mt-8 space-y-3">
              <button data-testid="reveal-reaction-btn" onClick={() => setReaction({ tier: data.tier, roast_text: data.roast?.content_text, roaster_handle: data.roast?.roaster_handle, roaster_avatar: data.roast?.roaster_avatar })}
                className="w-full py-3 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-white font-bold transition-colors flex items-center justify-center gap-2">
                <Video size={16} /> 🎥 Record your reaction
              </button>

              <div className="rounded-sm border border-white/10 bg-zinc-900/60 p-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-zinc-100 mb-1"><Globe size={15} className="text-brand" /> Make it public?</div>
                <p className="text-xs text-zinc-500 mb-3">You were roasted as a gift — nothing goes to the public feed unless <span className="text-zinc-300">you</span> say it's OK. You can change your mind anytime.</p>
                {data.consent_feed ? (
                  <div className="flex items-center gap-2">
                    <span data-testid="reveal-consent-on" className="flex items-center gap-1.5 text-sm text-tier-mild font-bold"><Check size={15} /> Public sharing allowed</span>
                    <button data-testid="reveal-consent-off" onClick={() => consent(false)} disabled={consenting} className="ml-auto text-xs text-zinc-400 hover:text-white underline">Make it private again</button>
                  </div>
                ) : (
                  <button data-testid="reveal-consent-btn" onClick={() => consent(true)} disabled={consenting}
                    className="w-full py-2.5 rounded-full bg-brand hover:bg-brand-hover text-black font-bold text-sm transition-colors disabled:opacity-60">
                    {consenting ? "Saving…" : "I'm OK with this going public"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {reaction && <ReactionRecorder open={!!reaction} item={reaction} onClose={() => setReaction(null)} />}
    </div>
  );
}

const Center = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center bg-background text-zinc-100 px-4">{children}</div>
);
